import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Output,
  afterNextRender,
  computed,
  effect,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UIRouterModule } from '@uirouter/angular';

import { CompanySearchResultsComponent } from 'src/app/app-common/components/company-search-results/company-search-results.component';
import { CompanyTreeComponent } from 'src/app/app-common/components/company-tree/company-tree.component';
import { CompanyTreeSearchService } from 'src/app/app-common/services/company-tree-search.service';
import { CompanyTreeService } from 'src/app/app-common/services/company-tree.service';
import { BranchStatus } from 'src/app/app-common/types/company-tree';
import { ComponentsModule } from 'src/app/components/components.module';

const GROWTH_DURATION_MS = 150;
const GROWTH_EASING = 'ease-in';

@Component({
  selector: 'select-company-tree',
  templateUrl: './select-company-tree.component.html',
  styleUrl: './select-company-tree.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ComponentsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    UIRouterModule,
    CompanyTreeComponent,
    CompanySearchResultsComponent,
  ],
  providers: [CompanyTreeService, CompanyTreeSearchService],
})
export class SelectCompanyTreeComponent {
  @Output() closeMenu = new EventEmitter<void>();

  protected readonly treeService = inject(CompanyTreeService);
  protected readonly searchService = inject(CompanyTreeSearchService);

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private beforeGrowthRect: DOMRect | null = null;

  protected readonly mode = computed(() =>
    this.searchService.query() ? 'search' : 'tree',
  );

  protected readonly showSpinner = computed(
    () =>
      this.treeService.initialStatus() === 'loading' ||
      (this.mode() === 'search' &&
        this.searchService.loading() &&
        this.searchService.results().length === 0),
  );

  protected readonly filterConfig = {
    placeholder: 'Search Sub-Company',
    borderless: true,
  };

  /**
   * The text equivalent of the panel's centered spinner as well as the
   * end-of-list spinners, none of which assistive technology can read: a
   * progressbar is not a legal child of a tree or a list, and a spinner
   * tells a screen reader nothing.
   */
  protected readonly statusMessage = computed(() => {
    if (this.mode() !== 'search') {
      return this.treeService.loadingMore() ? 'Loading more companies' : '';
    }

    if (this.searchService.loading()) {
      return this.searchService.results().length === 0
        ? 'Searching companies'
        : 'Loading more companies';
    }

    return this.treeService.loadingMore() ? 'Loading more companies' : '';
  });

  constructor() {
    void this.treeService.initialize();

    // Only the opening's first load swaps the spinner for the tree; search,
    // paging and branch expansion change the body's content without ever
    // putting the host through that same loading -> loaded transition, so
    // watching for it here is enough to limit the animation to that moment.
    let previousStatus: BranchStatus = this.treeService.initialStatus();
    effect(() => {
      const status = this.treeService.initialStatus();

      if (status === 'loading') {
        this.beforeGrowthRect = this.host.nativeElement.getBoundingClientRect();
      } else if (status === 'loaded' && previousStatus === 'loading') {
        const before = this.beforeGrowthRect;
        this.beforeGrowthRect = null;
        if (before) {
          afterNextRender(() => this.growToLoadedSize(before), {
            injector: this.injector,
          });
        }
      }

      previousStatus = status;
    });
  }

  private growToLoadedSize(before: DOMRect): void {
    const host = this.host.nativeElement;
    const after = host.getBoundingClientRect();

    if (before.width === after.width && before.height === after.height) {
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // No `fill`: once the animation ends, the stylesheet's own `max-content`
    // width and content height take over again, so the panel keeps following
    // its content instead of being pinned to this one size.
    host.animate(
      [
        { width: `${before.width}px`, height: `${before.height}px` },
        { width: `${after.width}px`, height: `${after.height}px` },
      ],
      { duration: GROWTH_DURATION_MS, easing: GROWTH_EASING },
    );
  }
}
